import type {
  Currency,
  Language,
  Region,
} from '@whoop/i18n/types/internationalization';
import { i18n } from '@whoop/i18n/lang/client';
import type { JoinFlowContent } from 'services/generated/growth-content-service/models/JoinFlowContent';
import type { ApplicationScheme } from 'services/generated/growth-content-service/models/ApplicationScheme';
import type { CheckoutPromo } from 'services/generated/membership-service-v2/models/CheckoutPromo';
import {
  getFirstVariant,
  getMembershipTypeApplicationSchemeForCurrency,
  getPromoDisclaimer,
  getReferrer,
} from 'ui';
import type { Product } from 'services/generated/commerce-service/models/Product';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import { bandMaterialSorter } from 'ui/utils/variantHelpers';
import { formatDisplayPrice } from 'lib/utils/formatHelpers';
import { formatPrice } from '@whoop/i18n/utils/priceHelpers';
import { ONYX_SKU } from 'ui/utils/constants/productConstants';
import {
  ANNUAL,
  TRIAL,
  TWO_YEAR,
  FAMILY,
  GIFT,
} from '@/lib/constants/memberships';
import type { SiteWidePromoContent } from '@/types/siteWidePromoTypes';
import {
  getAbsoluteDiscountAmount,
  getPermyriadDisplayValue,
} from '@/lib/utils/promoHelpers';
import { WRIST_BAND, WYW_PRODUCT_TYPE } from '@/lib/constants/wristBandProduct';

export const getLandingPageSubtextCopy = ({
  promoContent,
  promoInfo,
  currency,
  hasTrialMembership,
  language,
  region,
  isRafGiftFlow = false,
  skipMemSelection = '',
  skipMemFamilySize,
  annualPrice,
  isOnGCCTrialDepositExperiment,
  trialDepositValue,
}: {
  promoContent?: JoinFlowContent;
  promoInfo?: CheckoutPromo;
  currency: Currency;
  hasTrialMembership: boolean;
  language: Language;
  region: Region;
  isRafGiftFlow?: boolean;
  skipMemSelection?: string;
  skipMemFamilySize?: string;
  annualPrice?: string;
  isOnGCCTrialDepositExperiment: boolean;
  trialDepositValue?: string;
}): {
  secondLine?: string;
  mobileSubtext: string;
  desktopSubtext: string;
  imageSrc: string | undefined;
} => {
  // Each of the below if blocks returns - so order matters here:

  if (skipMemSelection) {
    let membershipText = '';
    let subtext = '';
    const secondLine: string | undefined = undefined;

    if (skipMemSelection !== GIFT) {
      if (skipMemSelection === ANNUAL) {
        membershipText = i18n.t('joinLandingPage:annualMembership');
      } else if (skipMemSelection === TWO_YEAR) {
        membershipText = i18n.t('joinLandingPage:24MonthMembership');
      } else if (skipMemSelection === TRIAL) {
        membershipText = i18n.t('joinLandingPage:priceToBegin', {
          priceToBegin: formatDisplayPrice(0, {
            currency,
            language,
            region,
          }),
        });
      } else if (skipMemSelection === FAMILY) {
        membershipText = i18n.t('joinLandingPage:familyMembership', {
          size: skipMemFamilySize,
        });
      }
      subtext = `${membershipText} | ${i18n.t(
        'joinLandingPage:whoop4Included',
      )}`;
    }

    if (skipMemSelection === GIFT) {
      return {
        imageSrc: undefined,
        desktopSubtext: i18n.t('joinLandingPage:giftWhoopForAsLowAs', {
          annualPrice,
        }),
        mobileSubtext: i18n.t('joinLandingPage:gift12MonthMembership'),
        secondLine: i18n.t('joinLandingPage:whoop4Included'),
      };
    }

    return {
      imageSrc: undefined,
      desktopSubtext: subtext,
      mobileSubtext: subtext,
      secondLine,
    };
  }

  // If partnerships/rafGift promo content has been populated, use that.
  // Eventually, that will contain the default text and RAF/Affiliate text as well
  if (promoContent && (promoContent.partnership_name || isRafGiftFlow)) {
    return {
      imageSrc: promoContent.content.image,
      desktopSubtext: promoContent.content.base_desktop_subtext,
      mobileSubtext: promoContent.content.base_mobile_subtext,
      secondLine: undefined,
    };
  }

  const referrer = getReferrer(promoInfo);
  if (referrer && promoInfo) {
    const validPromo = getMembershipTypeApplicationSchemeForCurrency(
      promoInfo.promo_code.application_schemes as ApplicationScheme[],
      currency,
    );
    const referrerText = getPromoDisclaimer({
      promo: validPromo,
      location: 'primary',
      referrer,
      currency,
      language,
      region,
    });
    return {
      imageSrc: referrer.avatar_url,
      desktopSubtext: referrerText,
      mobileSubtext: referrerText,
      secondLine: undefined,
    };
  }

  const desktopSubtext = hasTrialMembership
    ? i18n.t('joinLandingPage:joinWhoopForAsLowAs', {
        priceToBegin: isOnGCCTrialDepositExperiment
          ? trialDepositValue
          : formatDisplayPrice(0, { currency, language, region }),
      })
    : i18n.t('joinLandingPage:joinWhoopNoTrial');

  const priceToBegin = hasTrialMembership
    ? `${i18n.t('joinLandingPage:priceToBegin', {
        priceToBegin: `${formatDisplayPrice(0, {
          currency,
          language,
          region,
        })}`,
      })} | `
    : '';

  return {
    imageSrc: undefined,
    desktopSubtext,
    mobileSubtext: `${priceToBegin} ${i18n.t(
      'joinLandingPage:whoop4Included',
    )}`,
  };
};

interface SiteWideCopy {
  imageSrc?: string;
  desktopSubtext: string;
  mobileSubtext: string;
  secondLine?: string;
}
/**
  @description This is to handle site wide promos for 2024. This is a temporary solution until we can refactor promo system.
  Why is it temporary: This assumes that if there is siteWidePromoContent then a site wide promo is live.
  However any promotion in CT can be assigned content in Contentful.
*/
export const getSiteWidePromoCopy = ({
  siteWidePromoContent,
  promoInfo,
  product,
  currency,
}: {
  siteWidePromoContent: SiteWidePromoContent;
  promoInfo: CheckoutPromo;
  product: Variant;
  currency: Currency;
}): SiteWideCopy | undefined => {
  if (!siteWidePromoContent || !promoInfo) return undefined;
  const discountType = product.price?.discounted?.discount.discount_type;
  const referrer = getReferrer(promoInfo);

  const translationParams = {
    name: referrer?.first_name,
    amount: '',
  };
  if (!discountType) return undefined;

  if (discountType === 'relative') {
    if (!product.price?.discounted?.discount.permyriad) return undefined;
    translationParams.amount = String(getPermyriadDisplayValue(product));
  } else if (discountType === 'absolute') {
    const discountAmount = getAbsoluteDiscountAmount(product);
    if (!discountAmount) return undefined;
    translationParams.amount = formatPrice(discountAmount, currency, {
      showCents: false,
    });
  }

  const copy = i18n.t(
    `joinLandingPage:siteWidePromo.${
      discountType === 'relative'
        ? 'gave-you-percentage-off'
        : 'gave-you-amount-off'
    }`,
    translationParams,
  );

  return {
    imageSrc: referrer?.avatar_url,
    desktopSubtext: copy,
    mobileSubtext: copy,
  };
};

export const sortAndFilterLandingSwatches = (
  products: Product[],
): Product[] => {
  return products
    .filter(
      (product) =>
        product.product_type === WRIST_BAND ||
        product.product_type === WYW_PRODUCT_TYPE,
    )
    .sort((product1, product2) => {
      const variant1 = getFirstVariant(product1);
      const variant2 = getFirstVariant(product2);
      // Sort the Onyx band (default product) first
      if (variant1.sku === ONYX_SKU) return -1;
      if (variant2.sku === ONYX_SKU) return 1;
      return bandMaterialSorter(variant1, variant2);
    });
};

export const getLandingPageSecondaryCTAButtonTextKey = ({
  skipGiftSelection,
}: {
  skipGiftSelection: boolean;
}): string => {
  let textKey = 'buyAsGift';

  if (skipGiftSelection) {
    textKey = 'buyForYourself';
  }

  return textKey;
};
